import React, { useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF, MeshReflectorMaterial, BakeShadows, useProgress } from "@react-three/drei";
import { EffectComposer, Bloom, DepthOfField } from "@react-three/postprocessing";
import { easing } from "maath";
import { suspend } from "suspend-react";
import { Instances, Computers } from "./Computers";
import LoadingScreen from "./LoadingScreen";

// --- Gyro permission & helpers ---
function useGyroPermission() {
  const [gyroEnabled, setGyroEnabled] = React.useState(false);
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [isIOS, setIsIOS] = React.useState(false);
  const hasRequestedRef = React.useRef(false);

  const requestPermission = React.useCallback(async () => {
    if (hasRequestedRef.current) return;
    hasRequestedRef.current = true;

    let granted = false;
    try {
      // iOS Safari pathway (DeviceOrientation)
      if (
        typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function'
      ) {
        const res = await DeviceOrientationEvent.requestPermission();
        console.log('[gyro] DeviceOrientation permission:', res);
        granted = res === 'granted';
      }
      // Fallback: some WebKit builds expose only DeviceMotion request
      if (!granted &&
        typeof DeviceMotionEvent !== 'undefined' &&
        typeof DeviceMotionEvent.requestPermission === 'function') {
        const res2 = await DeviceMotionEvent.requestPermission();
        console.log('[gyro] DeviceMotion permission:', res2);
        granted = res2 === 'granted';
      }
    } catch (e) {
      console.warn('[gyro] Permission error:', e);
      // Ne pas afficher d'alerte ici, juste logger
    }

    if (granted) {
      setGyroEnabled(true);
      setShowPrompt(false);
      console.log('[gyro] Gyroscope activé ✓');
    } else {
      console.log('[gyro] Permission refusée ou non disponible');
      setGyroEnabled(false);
      setShowPrompt(false);
    }
  }, []);

  React.useEffect(() => {
    // Check if permission is needed (iOS 13+)
    const needsPermission =
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function';

    if (needsPermission) {
      setIsIOS(true);
      setShowPrompt(true);

      // Tenter de demander la permission au premier toucher PARTOUT sur l'écran
      const handleFirstTouch = () => {
        console.log('[gyro] Toucher détecté, demande de permission...');
        requestPermission();
        window.removeEventListener('touchstart', handleFirstTouch);
        window.removeEventListener('click', handleFirstTouch);
      };

      // Écouter les événements tactiles ET clics
      window.addEventListener('touchstart', handleFirstTouch, { once: true, passive: true });
      window.addEventListener('click', handleFirstTouch, { once: true });

      return () => {
        window.removeEventListener('touchstart', handleFirstTouch);
        window.removeEventListener('click', handleFirstTouch);
      };
    } else {
      // Auto-enable for non-iOS devices or older iOS
      console.log('[gyro] Activation automatique (non-iOS ou iOS ancien)');
      setGyroEnabled(true);
      setIsIOS(false);
      setShowPrompt(false);
    }
  }, [requestPermission]);

  return { gyroEnabled, showPrompt, isIOS, requestPermission };
}

// Map device orientation to a pleasant camera offset
function mapGyroToCamera(beta, gamma, alpha, isPortrait) {
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  // Convert to radians
  const betaRad = (beta * Math.PI) / 180;
  const gammaRad = (gamma * Math.PI) / 180;

  let x, y;

  if (isPortrait) {
    // En mode portrait sur iPhone
    // gamma (inclinaison gauche/droite) contrôle x
    // beta (inclinaison avant/arrière) contrôle y
    x = clamp(-gamma / 15, -3, 3); // Inversé et normalisé
    y = clamp((beta - 60) / 30, -2, 2); // 60° est environ vertical
  } else {
    // En mode paysage
    x = clamp(beta / 15, -3, 3);
    y = clamp(-gamma / 15, -2, 2);
  }

  const z = isPortrait ? 7 : 5.5; // Distance ajustée selon l'orientation

  return [x, y, z];
}

// Composant qui gère l'état du chargement
function Loader({ onLoadingComplete }) {
  const { active, progress } = useProgress();
  const [show, setShow] = useState(true);
  const [smoothProgress, setSmoothProgress] = useState(0);

  // Progrès lissé pour garantir une animation visible
  useEffect(() => {
    // Si le progrès réel augmente, on met à jour progressivement
    if (progress > smoothProgress) {
      const timer = setTimeout(() => {
        setSmoothProgress(prev => {
          const step = Math.max(1, (progress - prev) * 0.2);
          return Math.min(prev + step, progress);
        });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [progress, smoothProgress]);

  useEffect(() => {
    if (!active && progress === 100 && smoothProgress >= 99) {
      // Délai avant de masquer pour voir le 100%
      const timer = setTimeout(() => {
        setShow(false);
        // Notifier que le chargement est terminé
        if (onLoadingComplete) {
          onLoadingComplete();
        }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [active, progress, smoothProgress, onLoadingComplete]);

  return <LoadingScreen progress={smoothProgress} active={show} />;
}

export default function App({ onLoadingComplete }) {
  // Detect iPhone and orientation
  const isIphone = typeof navigator !== 'undefined' && /iPhone/.test(navigator.userAgent);
  // Use matchMedia for reliable orientation detection
  const mqlPortrait = window.matchMedia("(orientation: portrait)");
  const [isPortrait, setIsPortrait] = useState(mqlPortrait.matches);
  const [showGyroPrompt, setShowGyroPrompt] = useState(false);
  const [loadingDone, setLoadingDone] = useState(false);

  // Camera position: further back on iPhone portrait
  const cameraPosition = isIphone && isPortrait ? [0, 0, 7] : [-1.5, 1, 5.5];
  const { gyroEnabled, showPrompt, isIOS } = useGyroPermission();

  useEffect(() => {
    const handler = (e) => setIsPortrait(e.matches);
    mqlPortrait.addEventListener("change", handler);
    return () => mqlPortrait.removeEventListener("change", handler);
  }, []);

  // Gérer l'affichage du prompt gyroscope avec délai
  useEffect(() => {
    if (loadingDone && showPrompt) {
      // Attendre 2500ms (comme le texte du bas) avant d'afficher le prompt gyroscope
      const timer = setTimeout(() => {
        setShowGyroPrompt(true);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [loadingDone, showPrompt]);

  // Masquer le prompt gyroscope au premier clic/toucher
  useEffect(() => {
    if (showGyroPrompt) {
      const hidePrompt = () => {
        setShowGyroPrompt(false);
      };

      window.addEventListener('touchstart', hidePrompt, { once: true });
      window.addEventListener('click', hidePrompt, { once: true });

      return () => {
        window.removeEventListener('touchstart', hidePrompt);
        window.removeEventListener('click', hidePrompt);
      };
    }
  }, [showGyroPrompt]);

  const handleLoadingComplete = () => {
    setLoadingDone(true);
    if (onLoadingComplete) {
      onLoadingComplete();
    }
  };

  return (
    <>
      <Loader onLoadingComplete={handleLoadingComplete} />
      {showGyroPrompt && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            textAlign: 'center',
            padding: '20px 40px',
            backgroundColor: 'rgba(53, 193, 159, 0.95)',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            animation: 'fadeInPulse 1.6s ease-in, pulse 2s infinite 1.6s',
            pointerEvents: 'none',
            maxWidth: '80%',
          }}
        >
          <p style={{
            color: 'black',
            margin: 0,
            fontSize: '18px',
            fontWeight: '500',
            fontFamily: "'Codystar', cursive",  
          }}>
            Tap the screen<br />
            to interact with<br /> 
            camera<br />
             👆🎥👆<br />
          </p>
        </div>
      )}
      <style>{`
        @keyframes fadeInPulse {
          0% {
            opacity: 0;
            transform: translateX(-50%) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateX(-50%) scale(1);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: translateX(-50%) scale(1);
          }
          50% {
            opacity: 0.7;
            transform: translateX(-50%) scale(1.05);
          }
        }
      `}</style>
      <Canvas
        shadows
        dpr={[0.5, 0.75]}
        camera={{ position: cameraPosition, fov: 45, near: 1, far: 20 }}
        eventSource={document.getElementById("root")}
        eventPrefix="client"
      >
        {/* Lights */}
        <color attach="background" args={["black"]} />
        <hemisphereLight intensity={0.15} groundColor="black" />
        <spotLight
          position={[10, 20, 10]}
          angle={0.12}
          penumbra={1}
          intensity={1}
          castShadow
          shadow-mapSize={512}
        />
        {/* Main scene */}
        <group position={[0, -1, 0]}>
          <Instances>
            <Computers scale={0.5} />
          </Instances>
          <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[5, 5]} />
            <MeshReflectorMaterial
              blur={[200, 20]}
              resolution={512}
              mixBlur={1}
              mixStrength={40}
              roughness={1}
              depthScale={1.2}
              minDepthThreshold={0.4}
              maxDepthThreshold={1.4}
              color="#202020"
              metalness={0.8}
            />
          </mesh>
          <pointLight
            distance={1.5}
            intensity={1}
            position={[-0.15, 0.7, 0]}
            color="orange"
          />
        </group>
        {/* Postprocessing - Optimisé pour Android */}
        <EffectComposer disableNormalPass>
          <Bloom
            luminanceThreshold={0.4}
            mipmapBlur
            luminanceSmoothing={0.0}
            intensity={3}
          />
          <DepthOfField
            target={[5, 2, isIphone && isPortrait ? 7 : 13]}
            focalLength={1.15}
            bokehScale={3}
            height={256}
          />
        </EffectComposer>
        {/* Camera movements */}
        <CameraRig gyroEnabled={gyroEnabled} />
        {/* Performance helper */}
        <BakeShadows />
      </Canvas>
    </>
  );
}

function CameraRig({ gyroEnabled }) {
  const targetPosRef = React.useRef([ -1, 0.5, 5.5 ]); // desired cam pos
  const hasGyroDataRef = React.useRef(false);
  const lastOrientationRef = React.useRef({ alpha: 0, beta: 0, gamma: 0 });
  const initialOrientationRef = React.useRef(null);

  React.useEffect(() => {
    const handleOrientation = (event) => {
      const { alpha, beta, gamma } = event;
      if (beta == null || gamma == null) return;

      // Store the orientation data
      lastOrientationRef.current = { alpha, beta, gamma };

      // Stocker l'orientation initiale pour calibrage
      if (!initialOrientationRef.current) {
        initialOrientationRef.current = { alpha, beta, gamma };
        console.log('[gyro] Orientation initiale enregistrée:', { beta, gamma });
      }

      if (!hasGyroDataRef.current) {
        console.log('[gyro] ✓ Gyroscope actif ! Données reçues:', { beta, gamma });
        hasGyroDataRef.current = true;
      }

      // Determine if portrait mode
      const isPortrait = window.innerHeight > window.innerWidth;

      // Update target camera position
      targetPosRef.current = mapGyroToCamera(beta, gamma, alpha, isPortrait);
    };

    // Toujours écouter les événements, même si gyroEnabled est false au début
    // Car sur les anciens iOS, il n'y a pas de requestPermission
    window.addEventListener('deviceorientation', handleOrientation, true);

    // Also try devicemotion as fallback
    const handleMotion = (event) => {
      if (hasGyroDataRef.current) return; // If orientation works, ignore motion

      if (event.rotationRate && (event.rotationRate.alpha || event.rotationRate.beta || event.rotationRate.gamma)) {
        console.log('[gyro] DeviceMotion détecté (fallback)');
      }
    };
    window.addEventListener('devicemotion', handleMotion, true);

    // Check après 2 secondes si on reçoit des données
    const checkTimeout = setTimeout(() => {
      if (!hasGyroDataRef.current) {
        console.log('[gyro] ⚠️ Aucune donnée gyroscope reçue');
        console.log('[gyro] Causes possibles:');
        console.log('  - Permission refusée');
        console.log('  - Gyroscope non disponible sur cet appareil');
        console.log('  - Site non en HTTPS (sauf localhost)');
      }
    }, 2000);

    return () => {
      clearTimeout(checkTimeout);
      window.removeEventListener('deviceorientation', handleOrientation, true);
      window.removeEventListener('devicemotion', handleMotion, true);
    };
  }, []); // Ne dépend plus de gyroEnabled

  useFrame((state, delta) => {
    const isIphone = typeof navigator !== 'undefined' && /iPhone/.test(navigator.userAgent);
    const isPortrait = window.innerHeight > window.innerWidth;

    // If no gyro data yet, use pointer/mouse movement
    if (!hasGyroDataRef.current) {
      targetPosRef.current = [
        -1 + (state.pointer.x * state.viewport.width) / 3,
        (1 + state.pointer.y) / 2,
        isIphone && isPortrait ? 7 : 5.5,
      ];
    }

    // Smooth camera movement
    easing.damp3(state.camera.position, targetPosRef.current, 0.3, delta);
    state.camera.lookAt(0, 0, 0);
  });

  return null;
}

function Bun(props) {
  const { nodes } = useGLTF(suspend(suzi).default);
  return (
    <mesh receiveShadow castShadow geometry={nodes.mesh.geometry} {...props}>
      <meshStandardMaterial color="#222" roughness={0.5} />
    </mesh>
  );
}